import React, { useState } from 'react';

import { useAuth } from '@repo/common';
import { BaseButton } from '@repo/common';
import UserInfoBottomSheetModal from './UserInfoBottomSheetModal';
import { useDashboardBreakpoint } from '@repo/common';

import { ReactComponent as HumanIcon } from '@repo/common/assets/icons/28px/icon_28px_human.svg';
import { ReactComponent as CouponIcon } from '@repo/common/assets/icons/16px/icon_16px_payment-coupon_outline.svg';

const UserAvatar = ({ user, isDesktop, isTablet }) => {
  if (user?.profileUrl) {
    return (
      <img
        src={user.profileUrl}
        alt='profile'
        className={`${
          isDesktop
            ? 'w-[140px] h-[140px]'
            : isTablet
            ? 'w-[110px] h-[110px]'
            : 'w-[100px] h-[100px]'
        } rounded-[30px] object-cover`}
        style={{
          outline: '2px solid rgba(198, 249, 233, 0.20)',
          outlineOffset: '-2px',
        }}
      />
    );
  }

  return (
    <HumanIcon
      className={`${
        isDesktop
          ? 'w-[140px] h-[140px]'
          : isTablet
          ? 'w-[110px] h-[110px]'
          : 'w-[100px] h-[100px]'
      } text-grayscale-gray06`}
    />
  );
};

const UserInfo = () => {
  const { user } = useAuth();
  const bp = useDashboardBreakpoint();
  const isDesktop = ['desktopS', 'desktopL'].includes(bp);
  const isTablet = bp === 'tabletV';
  const [active, setActive] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);

  const getStepKey = (step) => `step ${String(step).padStart(2, '0')}`;
  const userStep = getStepKey(user?.step) || 'step 01';

  return (
    <div
      className={`flex flex-col w-full ${
        isDesktop
          ? 'max-w-[370px] max-h-[140px] h-full'
          : isTablet
          ? 'max-w-[180px]'
          : 'pb-9'
      }`}
    >
      {isDesktop ? (
        <div className='flex gap-4 w-full h-full'>
          <UserAvatar user={user} isDesktop={isDesktop} />
          <div className='flex flex-col justify-center items-start gap-1 w-full'>
            <div className='typo-title1 text-green-deep05'>
              {user?.firstName} {user?.lastName}
            </div>
            <div className='typo-b14Strong'>
              <span className='text-grayscale-gray03'>Lv </span>
              <span className='text-green-deep04'>step 01</span>
            </div>
            <div className='flex justify-end w-full'>
              <div className='relative inline-flex'>
                <BaseButton
                  icon={<CouponIcon />}
                  type='text_basic'
                  size='s'
                  gap='gap-0.5'
                >
                  Coupon
                </BaseButton>
                <span className='absolute top-[6px] right-[5px] w-[5px] h-[5px] rounded-full bg-purple-purple05' />
              </div>
            </div>
          </div>
        </div>
      ) : isTablet ? (
        <div className='flex flex-col gap-2 w-[180px]'>
          <UserAvatar user={user} isTablet={isTablet} />
          <div className='flex flex-col items-start gap-1 w-full'>
            <div className='typo-title3 text-green-deep05'>
              {user?.firstName} {user?.lastName}
            </div>
            <div className='typo-b14Strong'>
              <span className='text-grayscale-gray03'>Lv </span>
              <span className='text-green-deep04'>step 01</span>
            </div>
          </div>
          <div className='relative inline-flex'>
            <BaseButton
              icon={<CouponIcon />}
              type='text_basic'
              size='s'
              gap='gap-0.5'
            >
              Coupon
            </BaseButton>
            <span className='absolute top-[6px] left-[46%] w-[5px] h-[5px] rounded-full bg-purple-purple05' />
          </div>
        </div>
      ) : (
        <>
          <div
            className='flex items-center gap-4 p-3 rounded-[25px] bg-grayscale-gray00 w-full'
            style={{
              border: '1.6px solid transparent',
              backgroundImage: active
                ? `linear-gradient(#ffffff, #ffffff), linear-gradient(rgba(0, 158, 79, 0.4), rgba(0, 158, 79, 0.4)), linear-gradient(rgba(0, 158, 79, 0.4), rgba(0, 158, 79, 0.4))`
                : `linear-gradient(#ffffff, #ffffff), linear-gradient(130deg, rgba(198, 249, 233, 0.20) 13.25%, rgba(77, 213, 163, 0.20) 47.59%, rgba(198, 249, 233, 0.20) 81.94%), linear-gradient(rgba(85, 82, 78, 0.20), rgba(85, 82, 78, 0.10))`,
              backgroundOrigin: 'padding-box, border-box, border-box',
              backgroundClip: 'padding-box, border-box, border-box',
            }}
            onClick={() => {
              setActive((prev) => !prev);
              setIsMobileModalOpen(true);
            }}
          >
            <UserAvatar user={user} />
            <div className='flex flex-col items-start justify-center gap-1 flex-1'>
              <div className='typo-title2 text-green-deep05'>
                {user?.firstName} {user?.lastName}
              </div>
              <div className='typo-b14Strong'>
                <span className='text-grayscale-gray03'>Lv </span>
                <span className='text-green-deep04'>{userStep}</span>
              </div>
              <div className='relative inline-flex items-end ml-auto'>
                <BaseButton
                  icon={<CouponIcon />}
                  type='text_basic'
                  size='s'
                  gap='gap-0.5'
                >
                  Coupon
                </BaseButton>
                <span className='absolute top-[6px] right-[5px] w-[5px] h-[5px] rounded-full bg-purple-purple05' />
              </div>
            </div>
          </div>

          <UserInfoBottomSheetModal
            isOpen={isMobileModalOpen}
            onClose={() => {
              setActive(false);
              setIsMobileModalOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
};

export default UserInfo;
